/**
 * @fullvsl/app-shell/views/editor — VSL create + config editor (FUL-225).
 *
 * Two modes share the same form:
 *  - "create": blank form that creates a new VslProject on submit.
 *  - "edit": pre-filled form that patches an existing project on change.
 *
 * Fields: name, source type/URL, aspect ratio, autoplay, CTA config (reveal
 * mode/threshold/basedOn/destination), resume mode. Overlay editing happens in
 * the preview view (live player + OverlayEditor), not here — this view is the
 * structural config surface.
 */
import type {
  AspectRatio,
  CtaBasedOn,
  CtaRevealMode,
  Overlay,
  ProgressBarMode,
  ResumeMode,
  SourceType,
  VslProject,
} from "../../../contracts/src/contracts";
import { DEFAULT_PROGRESS_BAR_CONFIG } from "../../../contracts/src/contracts";
import { cloneOverlay, createDefaultOverlays } from "../../../contracts/src/overlay-templates";
import { OverlayEditor } from "../../../player/src/fullvsl-overlay-editor";
import { toVisualProgress } from "../../../player/src/progress-curve";
import { FIXTURE_MP4 } from "../../../contracts/src/mocks";
import type { VslStore, VslMutableFields } from "../store";
import { el, append, field } from "../dom";

const ASPECT_RATIOS: ReadonlyArray<{ id: AspectRatio; label: string }> = [
  { id: "auto", label: "Detectar automaticamente" },
  { id: "16:9", label: "16:9 Horizontal" },
  { id: "9:16", label: "9:16 Vertical" },
  { id: "1:1", label: "1:1 Quadrado" },
  { id: "4:5", label: "4:5 Vertical" },
  { id: "3:4", label: "3:4 Vertical" },
  { id: "custom", label: "Personalizado" },
];

const RESUME_MODES: ReadonlyArray<{ id: ResumeMode; label: string; hint: string }> = [
  { id: "ask", label: "Perguntar se deseja continuar", hint: "Exibir uma pergunta ao retornar" },
  { id: "always_resume", label: "Sempre continuar", hint: "Continuar automaticamente de onde parou" },
  { id: "always_restart", label: "Sempre reiniciar", hint: "Nunca continuar" },
];

type AutoplayPolicy = "muted" | "attempt_unmuted" | "off";

function autoplayPolicy(project?: VslProject): AutoplayPolicy {
  if (project?.autoplay.enabled === false) return "off";
  return project?.autoplay.muted === false ? "attempt_unmuted" : "muted";
}

export interface EditorOptions {
  mode: "create" | "edit";
  store: VslStore;
  /** For edit mode: the project id. Ignored for create. */
  projectId?: string;
  navigate: (path: string) => void;
}

export function renderEditorView(opts: EditorOptions): HTMLElement {
  const wrapper = el("div");
  let project: VslProject | undefined;
  if (opts.mode === "edit" && opts.projectId) {
    project = opts.store.get(opts.projectId);
    if (!project) {
      append(wrapper, renderNotFound());
      return wrapper;
    }
  }
  let overlayDraft: Overlay[] = project
    ? project.overlays.map(cloneOverlay)
    : createDefaultOverlays();

  const form = el("form", "fva-form");
  form.addEventListener("submit", (e) => e.preventDefault());

  // ---- Page header ----
  const header = el("div", "fva-page-header");
  const headerText = el("div");
  append(headerText, el("h2", null, opts.mode === "create" ? "Nova VSL" : (project?.name ?? "VSL")));
  append(headerText, el("p", null, opts.mode === "create" ? "Crie uma nova carta de vendas em vídeo." : "Configure o vídeo, o player e a chamada para ação."));
  append(header, headerText);
  append(wrapper, header);

  // ---- Tab bar (approved editor reference pattern) ----
  const tabs = el("div", "fva-tabs");
  const TABS = [
    { id: "video", label: "Vídeo" },
    { id: "player", label: "Player e retomada" },
    { id: "overlays", label: "Overlays" },
    { id: "cta", label: "CTA e atrasos" },
    { id: "tracking", label: "Rastreamento" },
  ] as const;
  const tabButtons: Record<string, HTMLButtonElement> = {};
  const tabPanels: Record<string, HTMLElement> = {};
  for (const t of TABS) {
    const tab = el("button", "fva-tab", t.label) as HTMLButtonElement;
    tab.type = "button";
    tabButtons[t.id] = tab;
    const panel = el("div");
    panel.style.display = "none";
    tabPanels[t.id] = panel;
    tab.addEventListener("click", () => {
      for (const tt of TABS) {
        tabButtons[tt.id].classList.toggle("is-active", tt.id === t.id);
        tabPanels[tt.id].style.display = tt.id === t.id ? "block" : "none";
      }
    });
    append(tabs, tab);
  }
  tabButtons.video.classList.add("is-active");
  tabPanels.video.style.display = "block";

  // ---- Section: Geral ----
  const generalSection = el("div", "fva-form-section");
  append(generalSection, el("h3", "fva-form-section-title", "Geral"));

  const nameInput = el("input", "fva-input") as HTMLInputElement;
  nameInput.type = "text";
  nameInput.placeholder = "Ex.: VSL de lançamento do produto";
  nameInput.value = project?.name ?? "";
  nameInput.required = true;
  append(generalSection, field("Nome da VSL", nameInput));

  const statusGroup = el("div", "fva-radio-group");
  const statusDraft = makeRadio("draft", "Rascunho", project?.status !== "published");
  const statusPub = makeRadio("published", "Publicada", project?.status === "published");
  append(statusGroup, statusDraft, statusPub);
  append(generalSection, field("Status", statusGroup, "VSLs publicadas ficam disponíveis para incorporação."));

  // ---- Section: Source ----
  const sourceSection = el("div", "fva-form-section");
  append(sourceSection, el("h3", "fva-form-section-title", "Fonte do vídeo"));

  const sourceTypeGroup = el("div", "fva-radio-group");
  const stFullFunnel = makeRadio("fullfunnel_media", "Mídia do Full Funnel", project?.sourceType !== "direct_url" && project?.sourceType !== "youtube", "sourceType");
  const stDirect = makeRadio("direct_url", "URL direta do MP4", project?.sourceType === "direct_url", "sourceType");
  const stYoutube = makeRadio("youtube", "YouTube", project?.sourceType === "youtube", "sourceType");
  append(sourceTypeGroup, stFullFunnel, stDirect, stYoutube);
  append(sourceSection, field("Tipo de fonte", sourceTypeGroup));

  const urlRow = el("div", "fva-field-row");
  const urlInput = el("input", "fva-input") as HTMLInputElement;
  urlInput.type = "url";
  urlInput.placeholder = "https://…";
  urlInput.value = project?.sourceUrl ?? FIXTURE_MP4;
  urlInput.required = true;
  append(urlRow, field("URL da fonte", urlInput, "Link direto do MP4 ou caminho da mídia no Full Funnel."));
  const preloadBtn = el("button", "fva-btn fva-btn-secondary fva-btn-sm", "Usar MP4 de exemplo") as HTMLButtonElement;
  preloadBtn.type = "button";
  preloadBtn.addEventListener("click", () => {
    urlInput.value = FIXTURE_MP4;
  });
  const preloadWrap = el("div", "fva-field");
  preloadWrap.style.paddingTop = "20px";
  append(preloadWrap, preloadBtn);
  append(urlRow, preloadWrap);
  append(sourceSection, urlRow);

  const ratioGroup = el("div", "fva-radio-group");
  const ratioBtns: Record<string, HTMLLabelElement> = {};
  for (const r of ASPECT_RATIOS) {
    const active = project?.aspectRatio === r.id;
    const label = makeRadio(r.id, r.label, active, "aspect");
    ratioBtns[r.id] = label;
    append(ratioGroup, label);
  }
  append(sourceSection, field("Proporção do vídeo", ratioGroup, "A detecção automática usa os metadados do vídeo."));

  // Personalizado ratio inputs (shown only when "custom" is selected)
  const customRow = el("div", "fva-field-row");
  customRow.style.display = project?.aspectRatio === "custom" ? "flex" : "none";
  const customW = el("input", "fva-input") as HTMLInputElement;
  customW.type = "number";
  customW.min = "1";
  customW.placeholder = "w";
  customW.value = String(project?.customRatio?.w ?? 16);
  const customH = el("input", "fva-input") as HTMLInputElement;
  customH.type = "number";
  customH.min = "1";
  customH.placeholder = "h";
  customH.value = String(project?.customRatio?.h ?? 9);
  append(customRow, field("Largura", customW), field("Altura", customH));
  append(sourceSection, customRow);

  // Wire aspect ratio toggle
  for (const r of ASPECT_RATIOS) {
    const input = ratioBtns[r.id].querySelector("input")!;
    input.addEventListener("change", () => {
      if (input.checked) {
        for (const key of Object.keys(ratioBtns)) {
          ratioBtns[key].classList.toggle("is-checked", key === r.id);
        }
        customRow.style.display = r.id === "custom" ? "flex" : "none";
      }
    });
  }

  // ---- Section: CTA ----
  const ctaSection = el("div", "fva-form-section");
  append(ctaSection, el("h3", "fva-form-section-title", "CTA com atraso"));
  append(ctaSection, el("p", "fva-vsl-meta", "Exibe a chamada para ação conforme o progresso assistido, não pelo tempo na página."));

  const revealModeGroup = el("div", "fva-radio-group");
  const rmPercent = makeRadio("percent", "% do vídeo assistido", project?.cta.revealMode !== "timestamp", "revealMode");
  const rmTimestamp = makeRadio("timestamp", "Segundos", project?.cta.revealMode === "timestamp", "revealMode");
  append(revealModeGroup, rmPercent, rmTimestamp);
  append(ctaSection, field("Gatilho de exibição", revealModeGroup));

  const ctaRow = el("div", "fva-field-row");
  const revealAtInput = el("input", "fva-input") as HTMLInputElement;
  revealAtInput.type = "number";
  revealAtInput.min = "0";
  revealAtInput.value = String(project?.cta.revealAt ?? 50);
  append(ctaRow, field("Limite de exibição", revealAtInput, "% (0–100) or seconds depending on trigger."));

  const basedOnGroup = el("div", "fva-radio-group");
  const boMax = makeRadio("maxWatched", "Máximo assistido", project?.cta.basedOn !== "currentTime", "basedOn");
  const boCurrent = makeRadio("currentTime", "Tempo atual", project?.cta.basedOn === "currentTime", "basedOn");
  append(basedOnGroup, boMax, boCurrent);
  append(ctaRow, field("Baseado em", basedOnGroup, "O máximo assistido evita manipulação ao avançar o vídeo."));
  append(ctaSection, ctaRow);

  const destInput = el("input", "fva-input") as HTMLInputElement;
  destInput.type = "url";
  destInput.placeholder = "https://example.com/offer";
  destInput.value = project?.cta.destination.value ?? "";
  append(ctaSection, field("URL de destino da CTA", destInput));

  const ctaTextRow = el("div", "fva-field-row");
  const headlineInput = el("input", "fva-input") as HTMLInputElement;
  headlineInput.placeholder = "Título";
  headlineInput.value = project?.ctaHeadline ?? "";
  append(ctaTextRow, field("Título da CTA", headlineInput));
  const btnLabelInput = el("input", "fva-input") as HTMLInputElement;
  btnLabelInput.placeholder = "Texto do botão";
  btnLabelInput.value = project?.ctaButtonLabel ?? "";
  append(ctaTextRow, field("Texto do botão da CTA", btnLabelInput));
  append(ctaSection, ctaTextRow);

  const subtextInput = el("textarea", "fva-textarea") as HTMLTextAreaElement;
  subtextInput.placeholder = "Texto complementar abaixo do título";
  subtextInput.value = project?.ctaSubtext ?? "";
  subtextInput.rows = 2;
  append(ctaSection, field("Subtexto da CTA", subtextInput, "Texto complementar opcional abaixo do título."));

  // ---- Section: Resume ----
  const resumeSection = el("div", "fva-form-section");
  append(resumeSection, el("h3", "fva-form-section-title", "Retomar reprodução"));
  const resumeGroup = el("div", "fva-radio-group");
  const resumeBtns: Record<string, HTMLLabelElement> = {};
  for (const rm of RESUME_MODES) {
    const active = project?.resume.mode === rm.id || (!project && rm.id === "ask");
    const label = makeRadio(rm.id, rm.label, active, "resumeMode");
    label.title = rm.hint;
    resumeBtns[rm.id] = label;
    append(resumeGroup, label);
  }
  append(resumeSection, field("Comportamento ao retornar", resumeGroup));

  const autoplaySelect = el("select", "fva-select") as HTMLSelectElement;
  const autoplayOptions: ReadonlyArray<{ id: AutoplayPolicy; label: string }> = [
    { id: "muted", label: "Reprodução automática sem som + toque para ativar" },
    { id: "attempt_unmuted", label: "Tentar reprodução automática com som" },
    { id: "off", label: "Não reproduzir automaticamente" },
  ];
  const currentAutoplay = autoplayPolicy(project);
  for (const option of autoplayOptions) {
    const node = document.createElement("option");
    node.value = option.id;
    node.textContent = option.label;
    node.selected = option.id === currentAutoplay;
    autoplaySelect.appendChild(node);
  }
  append(resumeSection, field("Política de reprodução automática", autoplaySelect, "Configuração exclusiva desta VSL."));

  const resumeThresholdInput = el("input", "fva-input") as HTMLInputElement;
  resumeThresholdInput.type = "number";
  resumeThresholdInput.min = "0";
  resumeThresholdInput.step = "1";
  resumeThresholdInput.value = String(project?.resume.thresholdSec ?? 10);
  append(
    resumeSection,
    field("Tempo mínimo para retomar (segundos)", resumeThresholdInput, "Tempo mínimo assistido antes de oferecer a retomada."),
  );

  const progressSection = el("div", "fva-form-section");
  append(progressSection, el("h3", "fva-form-section-title", "Barra de progresso dinâmica"));
  append(
    progressSection,
    el(
      "p",
      "fva-vsl-meta",
      "A barra avança mais rapidamente no início e desacelera perto do final. O tempo real do vídeo e as estatísticas não são alterados.",
    ),
  );

  const progressModeSelect = el("select", "fva-select") as HTMLSelectElement;
  progressModeSelect.setAttribute("aria-label", "Comportamento da barra de progresso");
  const currentProgress = project?.progressBar ?? DEFAULT_PROGRESS_BAR_CONFIG;
  for (const option of [
    { value: "accelerated", label: "Acelerada" },
    { value: "linear", label: "Tempo real" },
  ] as const) {
    const node = el("option", null, option.label) as HTMLOptionElement;
    node.value = option.value;
    node.selected = option.value === currentProgress.mode;
    progressModeSelect.appendChild(node);
  }
  append(progressSection, field("Comportamento", progressModeSelect, "Configuração exclusiva desta VSL."));

  const intensityWrap = el("div", "fva-progress-curve-control");
  const intensityInput = el("input") as HTMLInputElement;
  intensityInput.type = "range";
  intensityInput.min = "1.1";
  intensityInput.max = "2.5";
  intensityInput.step = "0.1";
  intensityInput.value = String(currentProgress.intensity);
  intensityInput.setAttribute("aria-label", "Intensidade da aceleração inicial");
  const intensityValue = el("output", "fva-progress-curve-value");
  append(intensityWrap, intensityInput, intensityValue);
  const intensityField = field(
    "Intensidade",
    intensityWrap,
    "Valores maiores fazem a barra avançar mais no início.",
  );
  const curvePreview = el("p", "fva-progress-curve-preview");
  const refreshProgressControl = () => {
    const accelerated = progressModeSelect.value === "accelerated";
    intensityField.style.display = accelerated ? "block" : "none";
    const intensity = Math.max(1.1, Math.min(2.5, Number(intensityInput.value) || 1.6));
    intensityValue.textContent = intensity.toLocaleString("pt-BR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const visualAtHalf = accelerated
      ? Math.round(toVisualProgress(0.5, { mode: "accelerated", intensity }) * 100)
      : 50;
    curvePreview.textContent = accelerated
      ? `Exemplo: com 50% do vídeo assistido, a barra mostrará aproximadamente ${visualAtHalf}%.`
      : "A barra acompanhará exatamente o tempo real do vídeo.";
  };
  progressModeSelect.addEventListener("change", refreshProgressControl);
  intensityInput.addEventListener("input", refreshProgressControl);
  append(progressSection, intensityField, curvePreview);
  refreshProgressControl();

  const brandingSection = el("div", "fva-form-section");
  append(brandingSection, el("h3", "fva-form-section-title", "Aparência do player"));
  const brandRow = el("div", "fva-field-row");
  const brandPicker = el("input", "fva-color-input") as HTMLInputElement;
  brandPicker.type = "color";
  brandPicker.value = project?.brandColor ?? "#00685f";
  const brandInput = el("input", "fva-input") as HTMLInputElement;
  brandInput.type = "text";
  brandInput.value = project?.brandColor ?? "#00685f";
  brandInput.pattern = "#[0-9a-fA-F]{6}";
  brandPicker.addEventListener("input", () => { brandInput.value = brandPicker.value; });
  brandInput.addEventListener("input", () => {
    if (/^#[0-9a-fA-F]{6}$/.test(brandInput.value)) brandPicker.value = brandInput.value;
  });
  append(brandRow, field("Cor", brandPicker), field("Cor hexadecimal", brandInput));
  append(brandingSection, brandRow);

  const trackingSection = el("div", "fva-form-section");
  append(trackingSection, el("h3", "fva-form-section-title", "Eventos da incorporação"));
  const emitLabel = document.createElement("label");
  emitLabel.className = "fva-check-row";
  const emitInput = document.createElement("input");
  emitInput.type = "checkbox";
  emitInput.checked = project?.emitAnalyticsToParent ?? true;
  emitLabel.append(emitInput, document.createTextNode(" Enviar eventos de análise para a página principal"));
  append(trackingSection, emitLabel);

  // ---- Section: Per-VSL overlays ----
  const overlaysSection = el("div", "fva-form-section");
  append(overlaysSection, el("h3", "fva-form-section-title", "Overlays desta VSL"));
  append(
    overlaysSection,
    el(
      "p",
      "fva-vsl-meta",
      "Estes overlays pertencem somente a esta VSL. Os modelos são copiados na criação e as alterações não afetam outros projetos.",
    ),
  );
  const overlayHost = el("div");
  overlayHost.style.marginTop = "16px";
  overlaysSection.appendChild(overlayHost);
  void new OverlayEditor({
    container: overlayHost,
    overlays: overlayDraft,
    onChange: (overlays) => {
      overlayDraft = overlays.map(cloneOverlay);
    },
  });

  // ---- Actions ----
  const actionsRow = el("div", "fva-action-bar");

  const saveBtn = el("button", "fva-btn fva-btn-primary", opts.mode === "create" ? "Criar VSL" : "Salvar alterações") as HTMLButtonElement;
  saveBtn.type = "submit";

  let deleteBtn: HTMLButtonElement | null = null;
  if (opts.mode === "edit" && project) {
    const finalProject = project;
    const db = el("button", "fva-btn fva-btn-danger", "Excluir") as HTMLButtonElement;
    db.type = "button";
    db.addEventListener("click", () => {
      if (confirm(`Excluir "${finalProject.name}"? Esta ação não pode ser desfeita.`)) {
        opts.store.remove(finalProject.id);
        opts.navigate("#/");
      }
    });
    deleteBtn = db;
  }

  append(actionsRow, saveBtn);
  if (deleteBtn) append(actionsRow, deleteBtn);

  // ---- Wire submit ----
  form.addEventListener("submit", () => {
    const name = nameInput.value.trim();
    if (!name) return;
    const sourceUrl = urlInput.value.trim();
    if (!sourceUrl) return;

    const sourceType = readRadioGroup(sourceTypeGroup) as SourceType;
    const aspectRatio = readRadioGroup(ratioGroup) as AspectRatio;
    const revealMode = readRadioGroup(revealModeGroup) as CtaRevealMode;
    const basedOn = readRadioGroup(basedOnGroup) as CtaBasedOn;
    const resumeMode = readRadioGroup(resumeGroup) as ResumeMode;
    const autoplay = autoplaySelect.value as AutoplayPolicy;
    const progressBar = {
      mode: progressModeSelect.value as ProgressBarMode,
      intensity: Math.max(1.1, Math.min(2.5, Number(intensityInput.value) || 1.6)),
    };
    const status = (readRadioGroup(statusGroup) as "draft" | "published") ?? "draft";

    const customRatio = aspectRatio === "custom"
      ? { w: Math.max(1, parseInt(customW.value) || 16), h: Math.max(1, parseInt(customH.value) || 9) }
      : undefined;

    const revealAt = Math.max(0, parseFloat(revealAtInput.value) || 0);
    const brandColor = /^#[0-9a-fA-F]{6}$/.test(brandInput.value)
      ? brandInput.value
      : "#00685f";
    const resume = {
      mode: resumeMode,
      thresholdSec: Math.max(0, parseInt(resumeThresholdInput.value, 10) || 0),
    };
    const autoplayConfig = {
      enabled: autoplay !== "off",
      muted: autoplay === "muted",
      promptToUnmute: autoplay !== "off",
      trackSeparately: true,
    };

    if (opts.mode === "create") {
      const created = opts.store.create({
        name,
        sourceType,
        sourceUrl,
        aspectRatio,
        customRatio,
        cta: {
          revealMode,
          revealAt,
          basedOn,
          rehideOnRewind: false,
          persistAfterReveal: true,
          destination: { type: "url", value: destInput.value.trim() },
        },
        resume,
        autoplay: autoplayConfig,
        progressBar,
        brandColor,
        emitAnalyticsToParent: emitInput.checked,
      });
      opts.store.setOverlays(created.id, overlayDraft.map(cloneOverlay));
      const ctaCopy: Partial<VslMutableFields> = {};
      if (headlineInput.value.trim()) ctaCopy.ctaHeadline = headlineInput.value.trim();
      if (subtextInput.value.trim()) ctaCopy.ctaSubtext = subtextInput.value.trim();
      if (btnLabelInput.value.trim()) ctaCopy.ctaButtonLabel = btnLabelInput.value.trim();
      if (Object.keys(ctaCopy).length > 0) opts.store.update(created.id, ctaCopy);
      opts.navigate(`#/vsl/${created.id}`);
    } else if (project) {
      const patch: VslMutableFields = {
        name,
        status,
        sourceType,
        sourceUrl,
        aspectRatio,
        customRatio,
        cta: {
          revealMode,
          revealAt,
          basedOn,
          rehideOnRewind: project.cta.rehideOnRewind,
          persistAfterReveal: project.cta.persistAfterReveal,
          destination: { type: "url", value: destInput.value.trim() },
        },
        resume,
        autoplay: autoplayConfig,
        progressBar,
        brandColor,
        emitAnalyticsToParent: emitInput.checked,
        overlays: overlayDraft.map(cloneOverlay),
        ctaHeadline: headlineInput.value.trim() || undefined,
        ctaSubtext: subtextInput.value.trim() || undefined,
        ctaButtonLabel: btnLabelInput.value.trim() || undefined,
      };
      opts.store.update(project.id, patch);
      opts.navigate(`#/vsl/${project.id}`);
    }
  });

  append(form, tabs);
  append(tabPanels.video, generalSection, sourceSection);
  append(tabPanels.player, resumeSection, progressSection, brandingSection);
  append(tabPanels.overlays, overlaysSection);
  append(tabPanels.cta, ctaSection);
  append(tabPanels.tracking, trackingSection);
  for (const t of TABS) append(form, tabPanels[t.id]);
  append(form, actionsRow);
  append(wrapper, form);

  // For edit mode: show quick links to preview + analytics
  if (opts.mode === "edit" && project) {
    const quickLinks = el("div", "fva-action-bar");
    const previewLink = el("button", "fva-btn fva-btn-secondary", "Visualizar player →");
    previewLink.addEventListener("click", () => opts.navigate(`#/vsl/${project.id}/preview`));
    const analyticsLink = el("button", "fva-btn fva-btn-secondary", "Ver estatísticas →");
    analyticsLink.addEventListener("click", () => opts.navigate(`#/vsl/${project.id}/analytics`));
    const publishLink = el("button", "fva-btn fva-btn-primary", "Publicar →");
    publishLink.addEventListener("click", () => opts.navigate(`#/vsl/${project.id}/publish`));
    append(quickLinks, previewLink, analyticsLink, publishLink);
    append(wrapper, quickLinks);
  }

  return wrapper;
}

function renderNotFound(): HTMLElement {
  const wrap = el("div", "fva-empty");
  append(wrap, el("h3", null, "VSL não encontrada"));
  append(wrap, el("p", null, "Este projeto pode ter sido excluído."));
  return wrap;
}

/** Create a radio-style label with a hidden input inside. */
function makeRadio(
  value: string,
  label: string,
  checked: boolean,
  name = "radio",
): HTMLLabelElement {
  const wrap = document.createElement("label");
  wrap.className = "fva-radio";
  if (checked) wrap.classList.add("is-checked");
  const input = document.createElement("input");
  input.type = "radio";
  input.name = name;
  input.value = value;
  input.checked = checked;
  input.style.margin = "0";
  input.addEventListener("change", () => {
    if (input.checked) {
      const group = wrap.parentElement;
      if (group) {
        for (const sibling of group.querySelectorAll(".fva-radio")) {
          sibling.classList.toggle("is-checked", sibling === wrap);
        }
      }
    }
  });
  wrap.appendChild(input);
  wrap.appendChild(document.createTextNode(" " + label));
  return wrap;
}

/** Read the selected value from a radio group container. */
function readRadioGroup(container: HTMLElement): string | null {
  const checked = container.querySelector<HTMLInputElement>("input[type=radio]:checked");
  return checked?.value ?? null;
}
